import { type FC, type PropsWithChildren, useEffect, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import type { XSellingDialogProductCardProps } from "@jti/commerce";
import { XSellingDialog, XSellingDialogEvents } from "@jti/commerce/features";
import { checkUrlEquivalence } from "@jti/fe-common/utils";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";

import { useLocale } from "@/context/LocaleContext";
import { useDisplayModelComponent } from "@/libs/builder/hooks/useDisplayModelComponent";

import { fetchXSellingPopupProducts } from "./utils/fetchXSellingPopupProducts";

import type { xSellingPopupInputs } from "./XSellingPopup.builder";

type XSellingPopupOpenEvent = CustomEvent<{
  skus?: string[];
}>;

export type XSellingPopupProps = BuilderComponentProps<typeof xSellingPopupInputs>;

export const X_SELLING_POPUP_GTM_CTA_LOCATION = "XSellingDialog";

const redirectOrReload = (targetUrl: string) => {
  if (checkUrlEquivalence(window.location.href, targetUrl)) {
    window.location.reload();
  } else {
    window.location.assign(targetUrl);
  }
};

export const XSellingPopup: FC<PropsWithChildren<XSellingPopupProps>> = ({
  title,
  subtitle,
  confirmButtonText,
  cancelButtonText,
  confirmRedirectUrl,
  showQuickAddToCartButton,
}) => {
  const { CART_CHECKOUT_PAGE_URL } = useConfig();
  const { currentLocale } = useLocale();
  const shouldDisplayModelComponent = useDisplayModelComponent("x-selling-popup");
  const [isOpen, setIsOpen] = useState(false);
  const [products, setProducts] = useState<XSellingDialogProductCardProps[]>([]);

  useEffect(() => {
    const abortController = new AbortController();

    const handleOpen = async (event: Event) => {
      const customEvent = event as XSellingPopupOpenEvent;
      const skus = customEvent.detail?.skus ?? [];

      try {
        const popupProducts = await fetchXSellingPopupProducts(skus, currentLocale);
        if (abortController.signal.aborted) return;
        setProducts(popupProducts);
        setIsOpen(skus.length === 0 || popupProducts.length > 0);
        if (skus.length > 0 && popupProducts.length === 0) {
          redirectOrReload(CART_CHECKOUT_PAGE_URL);
        }
      } catch {
        if (abortController.signal.aborted) return;
        setProducts([]);
        setIsOpen(false);
        redirectOrReload(CART_CHECKOUT_PAGE_URL);
      }
    };

    window.addEventListener(XSellingDialogEvents.open, handleOpen, {
      signal: abortController.signal,
    });

    return () => abortController.abort();
  }, [currentLocale, CART_CHECKOUT_PAGE_URL]);

  const handleConfirmClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: confirmButtonText,
      cta_location: X_SELLING_POPUP_GTM_CTA_LOCATION,
    });
    redirectOrReload(confirmRedirectUrl);
  };

  const handleCloseClick = async () => {
    await dispatchTrackingEvent(TrackingEvents.TrackingCtaClick, {
      cta_name: cancelButtonText,
      cta_location: X_SELLING_POPUP_GTM_CTA_LOCATION,
    });
    redirectOrReload(CART_CHECKOUT_PAGE_URL);
  };

  if (!shouldDisplayModelComponent) {
    return null;
  }

  return (
    isOpen && (
      <XSellingDialog
        isOpened={isOpen}
        onClose={handleCloseClick}
        onConfirm={handleConfirmClick}
        title={title}
        subtitle={subtitle}
        confirmButtonText={confirmButtonText}
        cancelButtonText={cancelButtonText}
        products={products}
        showQuickAddToCartButton={showQuickAddToCartButton}
      />
    )
  );
};
